import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowRight, Check, History, RefreshCw, ShieldAlert, X } from "lucide-react";
import { getCsrfToken, getFeeSettings, updateFeeSettings } from "../lib/api";
import { startGoogleSession } from "../lib/auth";
import {
  EXAMPLE_AMOUNT_CENTS,
  commissionCents,
  describePricing,
  fixedFeeToInput,
  formatFee,
  formatUsd,
  isLargeFeeChange,
  parseFixedFee,
  parsePercent,
  rateToPercent
} from "../lib/feeMath";
import type { FeeChange, FeeSettingsBundle, Viewer } from "../types";
import { useTimeZone } from "../lib/TimeZoneContext";
import { formatTimestamp } from "../lib/timeZone";
import { PageHeading } from "./PageHeading";
import { SkeletonRows } from "./Skeleton";
import { TableEmpty } from "./TableEmpty";
import { DEPLOYMENT_LABEL, DEPLOYMENT_NAME, EXPECTED_API_HOST, IS_PRODUCTION } from "../lib/runtime";

interface FeeSettingsPageProps {
  viewer: Viewer;
}

/** One history row's pricing, tolerating rows written before the flat fee existed. */
function historyPricing(rate: string, fixedFeeCents: number | null): string {
  const percent = rateToPercent(rate).percentDisplay;
  return fixedFeeCents === null ? `${percent}%` : describePricing(percent, fixedFeeCents);
}

export function FeeSettingsPage({ viewer }: FeeSettingsPageProps) {
  const { timeZone } = useTimeZone();
  const [bundle, setBundle] = useState<FeeSettingsBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newPercent, setNewPercent] = useState("");
  const [newFixedFee, setNewFixedFee] = useState("");
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setBundle(await getFeeSettings());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load commission settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const current = bundle ? rateToPercent(bundle.current.commissionRate) : null;
  const currentFixedFeeCents = bundle?.current.fixedFeeCents ?? 0;
  const proposed = parsePercent(newPercent);
  const proposedFixedFee = parseFixedFee(newFixedFee);

  const currentFee = current ? commissionCents(EXAMPLE_AMOUNT_CENTS, current.milliPercent, currentFixedFeeCents) : 0;
  const proposedFee = proposed && proposedFixedFee
    ? commissionCents(EXAMPLE_AMOUNT_CENTS, proposed.milliPercent, proposedFixedFee.cents)
    : null;

  // Compared on the total, so a flat fee typed in cents instead of dollars is
  // caught even though it leaves the percentage alone.
  const largeChange = proposedFee !== null && isLargeFeeChange(currentFee, proposedFee);
  const unchanged = Boolean(
    current && proposed && proposedFixedFee
    && current.rate === proposed.rate
    && currentFixedFeeCents === proposedFixedFee.cents
  );
  const reasonValid = reason.trim().length >= 3;
  const confirmPhrase = proposed && proposedFixedFee
    ? describePricing(proposed.percentDisplay, proposedFixedFee.cents)
    : "";
  const confirmationValid = !largeChange || confirmation.trim() === confirmPhrase;
  const canSave = Boolean(proposed && proposedFixedFee && !unchanged && reasonValid && confirmationValid && !saving);

  const openDialog = () => {
    if (!current) return;
    setNewPercent(current.percentDisplay);
    setNewFixedFee(fixedFeeToInput(currentFixedFeeCents));
    setReason("");
    setConfirmation("");
    setSaveError(null);
    dialogRef.current?.showModal();
  };

  const closeDialog = () => {
    if (!saving) dialogRef.current?.close();
  };

  const save = async () => {
    if (!bundle || !proposed || !proposedFixedFee || !canSave) return;
    let submitted = false;
    setSaving(true);
    setSaveError(null);
    try {
      await startGoogleSession();
      const csrfToken = await getCsrfToken();
      submitted = true;
      const updated = await updateFeeSettings(
        {
          commissionRate: proposed.rate,
          fixedFeeCents: proposedFixedFee.cents,
          expectedVersion: bundle.current.version,
          operationId: crypto.randomUUID(),
          reason: reason.trim(),
          csrfToken
        },
        viewer.email
      );
      setBundle(updated);
      dialogRef.current?.close();
    } catch (saveFailure) {
      if (!submitted) {
        setSaveError("Fresh Google authentication is required to change pricing. Your entries have been preserved.");
        return;
      }
      const status = (saveFailure as { status?: number }).status;
      if (status === 409) {
        setSaveError("Another admin changed pricing. Refresh and review the new values before saving.");
        await load();
      } else {
        setSaveError("The save result could not be confirmed. The current values have been refreshed; review them and the audit history before retrying.");
        await load();
      }
    } finally {
      setSaving(false);
    }
  };

  const lastChange: FeeChange | undefined = bundle?.history[0];

  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="SETTINGS / PRICING"
        title="Commission fee"
        description="A percentage of every gift plus a flat fee per card. Changes are immediate and audited."
        info={{
          label: "the connected environment",
          children: <><strong>Connected to the GCP {DEPLOYMENT_NAME} stack.</strong> {EXPECTED_API_HOST} · {IS_PRODUCTION ? "Live transfers" : "Sandbox transfers only"}.</>
        }}
        actions={<>
          <span className="dev-pill">{DEPLOYMENT_LABEL}</span>
          <button className="secondary-button" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={16} className={loading ? "spin" : ""} aria-hidden="true" />
            Refresh
          </button>
        </>}
      />

      {error ? (
        <section className="error-panel" role="alert">
          <AlertTriangle size={22} aria-hidden="true" />
          <div><strong>Commission settings unavailable</strong><p>{error}</p></div>
        </section>
      ) : null}

      <section className="rate-card" aria-busy={loading}>
        <div className="rate-card-header">
          <div>
            <p className="section-label">CURRENT PRICING</p>
            {loading || !current ? <div className="skeleton rate-skeleton" /> : (
              <p className="rate-value">
                {current.percentDisplay}<span>%</span>
                {currentFixedFeeCents > 0 ? <em className="rate-addend"> + {formatUsd(currentFixedFeeCents)} per card</em> : null}
              </p>
            )}
          </div>
          {viewer.role === "superAdmin" ? (
            <button className="primary-button" onClick={openDialog} disabled={!bundle || loading}>
              Change pricing <ArrowRight size={17} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {!loading && current ? (
          <div className="worked-example">
            <div className="example-icon"><span>$</span></div>
            <div>
              <p>On a <strong>$100.00 gift</strong>, the sender pays</p>
              <strong>{currentFee === 0 ? "No Fee" : `${formatUsd(currentFee)} in fees`}</strong>
              {currentFixedFeeCents > 0 ? (
                <p className="example-breakdown">
                  {formatUsd(commissionCents(EXAMPLE_AMOUNT_CENTS, current.milliPercent))} percentage
                  {" + "}{formatUsd(currentFixedFeeCents)} per card
                </p>
              ) : null}
            </div>
            <div className="example-total">
              <span>Sender total</span>
              <strong>{formatUsd(EXAMPLE_AMOUNT_CENTS + currentFee)}</strong>
            </div>
          </div>
        ) : null}
        {lastChange ? (
          <div className="last-change">
            <Check size={16} aria-hidden="true" />
            <span>Last changed {formatTimestamp(lastChange.at, timeZone)} by {lastChange.actorEmail}</span>
            <span className="change-reason">“{lastChange.reason}”</span>
          </div>
        ) : null}
      </section>

      <section className="history-card">
        <div className="section-heading-row">
          <div>
            <p className="section-label">AUDIT TRAIL</p>
            <h2>Change history</h2>
          </div>
          <div className="history-count"><History size={16} aria-hidden="true" /> {bundle?.history.length ?? 0} changes</div>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Changed</th><th>Actor</th><th>Pricing change</th><th>Reason</th></tr></thead>
            <tbody>
              {bundle?.history.map((entry) => (
                <tr key={`${entry.at}-${entry.actorEmail}`}>
                  <td>{formatTimestamp(entry.at, timeZone)}</td>
                  <td>{entry.actorEmail}</td>
                  <td className="rate-change-cell">
                    <span>{historyPricing(entry.oldRate, entry.oldFixedFeeCents)}</span>
                    <ArrowRight size={14} aria-hidden="true" />
                    <strong>{historyPricing(entry.newRate, entry.newFixedFeeCents)}</strong>
                  </td>
                  <td className="wrap-cell">{entry.reason}</td>
                </tr>
              ))}
              {loading && !bundle?.history.length ? <SkeletonRows columns={4} rows={4} /> : null}
              {!loading && bundle?.history.length === 0 ? (
                <TableEmpty columns={4} filtered={false} emptyMessage="No recorded changes yet" />
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <dialog className="rate-dialog" ref={dialogRef} onCancel={(event) => { event.preventDefault(); closeDialog(); }}>
        <div className="dialog-header">
          <div>
            <p className="eyebrow">PRICING CONTROL</p>
            <h2>Change commission pricing</h2>
          </div>
          <button className="icon-button" onClick={closeDialog} aria-label="Close dialog" disabled={saving}><X size={20} /></button>
        </div>

        <div className="rate-comparison">
          <div>
            <span>Current</span>
            <strong>{current ? describePricing(current.percentDisplay, currentFixedFeeCents) : "—"}</strong>
          </div>
          <ArrowRight size={18} aria-hidden="true" />
          <div className="pricing-inputs">
            <label>
              <span>Percentage</span>
              <span className="percent-input">
                <input
                  autoFocus
                  inputMode="decimal"
                  value={newPercent}
                  onChange={(event) => { setNewPercent(event.target.value); setConfirmation(""); }}
                  aria-describedby="rate-help"
                />
                <b>%</b>
              </span>
            </label>
            <label>
              <span>Per card</span>
              <span className="percent-input is-currency">
                <b>$</b>
                <input
                  inputMode="decimal"
                  value={newFixedFee}
                  onChange={(event) => { setNewFixedFee(event.target.value); setConfirmation(""); }}
                  aria-describedby="rate-help"
                />
              </span>
            </label>
          </div>
        </div>
        <p id="rate-help" className="field-help">
          Percentage greater than 0 and no more than 100, up to 3 decimal places. Per-card fee in
          dollars, up to $100.00 — enter <strong>0.49</strong> for 49 cents, or <strong>0</strong> for no flat fee.
        </p>

        <div className="impact-panel">
          <p className="section-label">EFFECT ON A $100.00 GIFT</p>
          <div className="impact-row">
            <span>Percentage</span>
            <span>
              {current ? formatUsd(commissionCents(EXAMPLE_AMOUNT_CENTS, current.milliPercent)) : "—"}
              {" "}<ArrowRight size={14} />{" "}
              <strong>{proposed ? formatUsd(commissionCents(EXAMPLE_AMOUNT_CENTS, proposed.milliPercent)) : "—"}</strong>
            </span>
          </div>
          <div className="impact-row">
            <span>Per card</span>
            <span>
              {formatUsd(currentFixedFeeCents)} <ArrowRight size={14} />{" "}
              <strong>{proposedFixedFee ? formatUsd(proposedFixedFee.cents) : "—"}</strong>
            </span>
          </div>
          <div className="impact-row is-total">
            <span>Total fee</span>
            <span>
              {formatFee(currentFee)} <ArrowRight size={14} />{" "}
              <strong>{proposedFee === null ? "—" : formatFee(proposedFee)}</strong>
            </span>
          </div>
          <div className="impact-row">
            <span>Sender pays</span>
            <span>
              {formatUsd(EXAMPLE_AMOUNT_CENTS + currentFee)} <ArrowRight size={14} />{" "}
              <strong>{proposedFee === null ? "—" : formatUsd(EXAMPLE_AMOUNT_CENTS + proposedFee)}</strong>
            </span>
          </div>
        </div>

        <div className="warning-copy">
          <ShieldAlert size={19} aria-hidden="true" />
          <p>
            This applies to every new gift from the moment you save, except customers with their own
            pricing. Gifts already in flight keep their original fee.
          </p>
        </div>

        <label className="field-label">Reason <span>Required</span>
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} placeholder="Why is pricing changing?" />
        </label>

        {largeChange ? (
          <label className="field-label confirmation-field">Confirm the new pricing <span>Large change</span>
            <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={`Type ${confirmPhrase}`} />
          </label>
        ) : null}

        {saveError ? <div className="form-error" role="alert">{saveError}</div> : null}

        <div className="dialog-actions">
          <button className="secondary-button" onClick={closeDialog} disabled={saving}>Cancel</button>
          <button className="primary-button" onClick={() => void save()} disabled={!canSave}>
            {saving ? "Saving…" : "Review & save"}
          </button>
        </div>
      </dialog>
    </div>
  );
}
